function newStableId(): string {
  return `bq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const BETA_QUESTION_KINDS = [
  'short_text',
  'paragraph',
  'multiple_choice',
  'checkboxes',
  'linear_scale',
] as const;

export type BetaQuestionKind = (typeof BETA_QUESTION_KINDS)[number];

type QuestionCommon = {
  id: string;
  prompt: string;
  order: number;
  /** When false, an empty answer is accepted. Default true. */
  required?: boolean;
};

export type BetaShortTextQuestion = QuestionCommon & { kind: 'short_text' };

export type BetaParagraphQuestion = QuestionCommon & { kind: 'paragraph' };

export type BetaMultipleChoiceQuestion = QuestionCommon & {
  kind: 'multiple_choice';
  options: string[];
};

export type BetaCheckboxesQuestion = QuestionCommon & {
  kind: 'checkboxes';
  options: string[];
};

export type BetaLinearScaleQuestion = QuestionCommon & {
  kind: 'linear_scale';
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
};

export type BetaReaderQuestionnaireQuestion =
  | BetaShortTextQuestion
  | BetaParagraphQuestion
  | BetaMultipleChoiceQuestion
  | BetaCheckboxesQuestion
  | BetaLinearScaleQuestion;

export function isQuestionRequired(q: BetaReaderQuestionnaireQuestion): boolean {
  return q.required !== false;
}

/** English UI labels for question kinds (desktop + web questionnaire editors). */
export const BETA_QUESTION_KIND_LABELS: Record<BetaQuestionKind, string> = {
  short_text: 'Short answer',
  paragraph: 'Paragraph',
  multiple_choice: 'Multiple choice',
  checkboxes: 'Checkboxes',
  linear_scale: 'Linear scale',
};

export function newBetaQuestionnaireEditorRowId(): string {
  return `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** New empty question row for the given type (matches web “Add …” behavior). */
export function createBetaQuestionOfKind(
  kind: BetaQuestionKind,
  order: number,
  id: string = newBetaQuestionnaireEditorRowId()
): BetaReaderQuestionnaireQuestion {
  const base = { id, prompt: '', order, required: true as const };
  switch (kind) {
    case 'short_text':
      return { ...base, kind: 'short_text' };
    case 'paragraph':
      return { ...base, kind: 'paragraph' };
    case 'multiple_choice':
      return { ...base, kind: 'multiple_choice', options: ['Option 1', 'Option 2'] };
    case 'checkboxes':
      return { ...base, kind: 'checkboxes', options: ['Option 1', 'Option 2'] };
    case 'linear_scale':
      return { ...base, kind: 'linear_scale', scaleMin: 1, scaleMax: 5 };
  }
}

/** Change `kind` while keeping id / prompt / order / required when possible. */
export function betaQuestionnaireQuestionWithKind(
  q: BetaReaderQuestionnaireQuestion,
  kind: BetaQuestionKind
): BetaReaderQuestionnaireQuestion {
  if (q.kind === kind) return q;
  const { id, prompt, order, required } = q;
  const base = { id, prompt, order, required: required !== false };
  switch (kind) {
    case 'short_text':
      return { ...base, kind: 'short_text' };
    case 'paragraph':
      return { ...base, kind: 'paragraph' };
    case 'multiple_choice':
      return { ...base, kind: 'multiple_choice', options: ['Option 1', 'Option 2'] };
    case 'checkboxes':
      return { ...base, kind: 'checkboxes', options: ['Option 1', 'Option 2'] };
    case 'linear_scale':
      return { ...base, kind: 'linear_scale', scaleMin: 1, scaleMax: 5 };
  }
}

export interface BookBetaReaderQuestionnaire {
  version: number;
  questions: BetaReaderQuestionnaireQuestion[];
  updatedAt: string;
}

export interface BetaReaderChapterAnswer {
  id: string;
  questionId: string;
  questionText: string;
  answerText: string;
  answeredAt: string;
}

export interface BetaReaderChapterSubmission {
  id: string;
  chapterId: string;
  readerUserId: string;
  readerDisplayName?: string;
  templateVersion: number;
  questionsSnapshot: BetaReaderQuestionnaireQuestion[];
  submittedAt: string;
  answers: BetaReaderChapterAnswer[];
}

function parseKind(raw: unknown): BetaQuestionKind {
  if (typeof raw === 'string' && (BETA_QUESTION_KINDS as readonly string[]).includes(raw)) {
    return raw as BetaQuestionKind;
  }
  return 'paragraph';
}

function parseOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const x of raw) {
    if (typeof x !== 'string') continue;
    const t = x.trim();
    if (t) out.push(t);
  }
  return [...new Set(out)];
}

function clampInt(n: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  const x = Math.round(n);
  return Math.min(hi, Math.max(lo, x));
}

function parseScaleBounds(o: Record<string, unknown>): { min: number; max: number } {
  let min = typeof o.scaleMin === 'number' ? o.scaleMin : Number(o.scaleMin);
  let max = typeof o.scaleMax === 'number' ? o.scaleMax : Number(o.scaleMax);
  if (!Number.isFinite(min)) min = 1;
  if (!Number.isFinite(max)) max = 5;
  min = clampInt(min, 0, 99, 1);
  max = clampInt(max, 0, 99, 5);
  if (max <= min) max = min + 1;
  if (max > 100) max = 100;
  return { min, max };
}

function normalizeOptionsForKind(kind: BetaQuestionKind, options: string[]): string[] {
  if (kind !== 'multiple_choice' && kind !== 'checkboxes') return options;
  if (options.length >= 2) return options;
  return ['Option 1', 'Option 2'];
}

/** One row from JSON / DB → discriminated question (drops invalid empty prompts). */
export function parseQuestionnaireQuestionsJson(raw: unknown): BetaReaderQuestionnaireQuestion[] {
  if (!Array.isArray(raw)) return [];
  const out: BetaReaderQuestionnaireQuestion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === 'string' && o.id ? o.id : newStableId();
    const prompt = typeof o.prompt === 'string' ? o.prompt.trim() : '';
    const order = typeof o.order === 'number' && Number.isFinite(o.order) ? o.order : out.length;
    if (!prompt) continue;

    const required = o.required === false ? false : true;
    const base = { id, prompt, order, required };

    const kind = parseKind(o.kind);
    if (kind === 'short_text') {
      out.push({ ...base, kind: 'short_text' });
      continue;
    }
    if (kind === 'paragraph') {
      out.push({ ...base, kind: 'paragraph' });
      continue;
    }
    if (kind === 'multiple_choice') {
      const options = normalizeOptionsForKind('multiple_choice', parseOptions(o.options));
      out.push({ ...base, kind: 'multiple_choice', options });
      continue;
    }
    if (kind === 'checkboxes') {
      const options = normalizeOptionsForKind('checkboxes', parseOptions(o.options));
      out.push({ ...base, kind: 'checkboxes', options });
      continue;
    }
    if (kind === 'linear_scale') {
      const { min, max } = parseScaleBounds(o);
      const scaleMinLabel =
        typeof o.scaleMinLabel === 'string' ? o.scaleMinLabel.trim().slice(0, 120) : undefined;
      const scaleMaxLabel =
        typeof o.scaleMaxLabel === 'string' ? o.scaleMaxLabel.trim().slice(0, 120) : undefined;
      out.push({
        ...base,
        kind: 'linear_scale',
        scaleMin: min,
        scaleMax: max,
        scaleMinLabel: scaleMinLabel || undefined,
        scaleMaxLabel: scaleMaxLabel || undefined,
      });
      continue;
    }
    out.push({ ...base, kind: 'paragraph' });
  }
  return sortQuestionnaireQuestions(out);
}

export function sortQuestionnaireQuestions(
  questions: BetaReaderQuestionnaireQuestion[]
): BetaReaderQuestionnaireQuestion[] {
  return [...questions].sort((a, b) => a.order - b.order || a.prompt.localeCompare(b.prompt));
}

export function validateQuestionnaireQuestions(
  questions: BetaReaderQuestionnaireQuestion[]
): { ok: true } | { ok: false; error: string } {
  for (const q of questions) {
    if (q.kind === 'multiple_choice' || q.kind === 'checkboxes') {
      if (q.options.length < 2) {
        return { ok: false, error: `“${q.prompt}” needs at least two non-empty choices.` };
      }
    }
    if (q.kind === 'linear_scale') {
      if (q.scaleMax <= q.scaleMin) {
        return { ok: false, error: `“${q.prompt}” needs scale max greater than min.` };
      }
      if (q.scaleMax - q.scaleMin > 99) {
        return { ok: false, error: `“${q.prompt}” scale range is too large.` };
      }
    }
  }
  return { ok: true };
}

/** Default prompts when a book has no questionnaire row yet. */
export function createDefaultBetaReaderQuestionnaireQuestions(): BetaReaderQuestionnaireQuestion[] {
  const prompts = [
    'What stood out to you most in this chapter?',
    'Where (if anywhere) did you feel confused or unclear?',
    'How did the pacing feel in this chapter?',
    'Which character moment felt most believable or least believable?',
    'What is one thing the author should keep or lean into?',
  ];
  return prompts.map((prompt, i) => ({
    id: newStableId(),
    kind: 'paragraph' as const,
    prompt,
    order: i,
    required: true,
  }));
}

/** Baseline end-of-book survey for beta qualification. */
export function createDefaultBetaEndSurveyQuestions(): BetaReaderQuestionnaireQuestion[] {
  const prompts = [
    'What did you think about the book?',
    'What was your favorite part?',
    'Who was your favorite character and why?',
    'What was your least favorite part?',
    'Who was your least favorite character and why?',
  ];
  return prompts.map((prompt, i) => ({
    id: newStableId(),
    kind: 'paragraph' as const,
    prompt,
    order: i,
    required: true,
  }));
}

/** Normalize a single answer value to the string we persist in `answerText`. */
export function normalizeAnswerForStorage(
  q: BetaReaderQuestionnaireQuestion,
  raw: unknown
): { ok: true; value: string } | { ok: false; error: string } {
  const required = isQuestionRequired(q);

  if (q.kind === 'short_text' || q.kind === 'paragraph') {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) {
      if (!required) return { ok: true, value: '' };
      return { ok: false, error: `Missing answer for: ${q.prompt}` };
    }
    return { ok: true, value: text };
  }

  if (q.kind === 'multiple_choice') {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) {
      if (!required) return { ok: true, value: '' };
      return { ok: false, error: `Missing answer for: ${q.prompt}` };
    }
    if (!q.options.includes(text)) {
      return { ok: false, error: `Invalid choice for: ${q.prompt}` };
    }
    return { ok: true, value: text };
  }

  if (q.kind === 'checkboxes') {
    let selected: string[] = [];
    if (Array.isArray(raw)) {
      selected = raw.filter((x): x is string => typeof x === 'string').map((x) => x.trim());
    } else if (typeof raw === 'string') {
      const t = raw.trim();
      if (t.startsWith('[')) {
        try {
          const parsed = JSON.parse(t) as unknown;
          if (Array.isArray(parsed)) {
            selected = parsed.filter((x): x is string => typeof x === 'string').map((x) => x.trim());
          }
        } catch {
          selected = [];
        }
      } else if (t) {
        selected = t.split(',').map((s) => s.trim());
      }
    }
    selected = [...new Set(selected)].filter((s) => q.options.includes(s));
    if (selected.length === 0) {
      if (!required) return { ok: true, value: '' };
      return { ok: false, error: `Select at least one option for: ${q.prompt}` };
    }
    return { ok: true, value: JSON.stringify(selected) };
  }

  if (q.kind === 'linear_scale') {
    const n = typeof raw === 'number' ? raw : Number(typeof raw === 'string' ? raw.trim() : raw);
    if (!Number.isFinite(n) || Math.round(n) !== n) {
      if (!required) return { ok: true, value: '' };
      return { ok: false, error: `Missing or invalid scale for: ${q.prompt}` };
    }
    const v = Math.round(n);
    if (v < q.scaleMin || v > q.scaleMax) {
      return { ok: false, error: `Scale out of range for: ${q.prompt}` };
    }
    return { ok: true, value: String(v) };
  }

  const _exhaustive: never = q;
  void _exhaustive;
  return { ok: false, error: 'Unsupported question type' };
}

/** Human-readable line for submission history (desktop + web). */
export function formatStoredAnswerForDisplay(
  q: BetaReaderQuestionnaireQuestion | undefined,
  answerText: string
): string {
  if (!answerText) return '';
  if (q?.kind === 'checkboxes') {
    try {
      const parsed = JSON.parse(answerText) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter((x): x is string => typeof x === 'string').join(', ');
      }
    } catch {
      /* fall through */
    }
  }
  if (q?.kind === 'linear_scale' && q.scaleMinLabel && q.scaleMaxLabel) {
    return `${answerText} (${q.scaleMinLabel} → ${q.scaleMaxLabel})`;
  }
  return answerText;
}
